import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { App, Card, Typography, Spin, Button, Alert } from 'antd';
import { useAuthStore } from '@/modules/auth/authStore';
import { useAppStore } from '@/stores/appStore';
import { LevelUser_81_TimIT } from '@/modules/data/user/types';
import { gradients, shadows } from '@/theme';
import type { LoginResponse } from '@/types/api';
import api from '@/services/api';

const { Title, Text } = Typography;

interface SsoErrorPayload {
  code: string;
  message: string;
}

interface SsoLoginEnvelope {
  success: boolean;
  data?: LoginResponse;
  error?: SsoErrorPayload;
}

/**
 * Halaman SSO Login — endpoint redirect dari Booku Client
 * (https://app.booku.id/sso-login?token=<jwt>).
 *
 * Alur:
 * 1. Baca token dari query string. Kosong → redirect ke /login.
 * 2. POST token ke /api/auth/sso-login.
 * 3. Sukses → simpan accessToken/refreshToken, set authStore + appStore,
 *    navigate ke '/'.
 * 4. Gagal → tampilkan pesan + tombol "Login Manual".
 *
 * SSO token TIDAK pernah disimpan ke localStorage (one-time exchange).
 */
export default function SsoLoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const { message } = App.useApp();
  const exchangeStarted = useRef(false);

  const params = new URLSearchParams(location.search);
  const token = params.get('token');

  const [error, setError] = useState<SsoErrorPayload | null>(null);

  useEffect(() => {
    if (!token) return;
    // Guard StrictMode double-mount: pastikan token tidak ditukar 2x
    if (exchangeStarted.current) return;
    exchangeStarted.current = true;

    const exchange = async () => {
      try {
        const response = await api.post<SsoLoginEnvelope>('/auth/sso-login', {
          ssoToken: token,
        });

        const envelope = response.data;
        if (!envelope.success || !envelope.data) {
          setError(envelope.error ?? {
            code: 'SSO_TOKEN_INVALID',
            message: 'Login SSO gagal.',
          });
          return;
        }

        const data = envelope.data;
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        login(data.user, data.tenant);

        // KarantinaCoa WAJIB ikut di-set eksplisit: `setAppInfo` MENGGABUNG ke state lama,
        // jadi bila dilewat nilainya sisa sesi sebelumnya — tenant 5 digit masuk dengan
        // sidebar penuh (setiap menu 409 tanpa penjelasan), atau sebaliknya tenant normal
        // terkunci tampilan karantina. Disamakan dengan LoginPage (temuan 19-10-2026).
        const karantinaCoa = data.karantinaCoa === true;
        useAppStore.getState().setAppInfo({
          TahunBukuAktif: data.tahunBukuAktif,
          TahunBukuKemarin: data.tahunBukuKemarin,
          JenisTahunBuku: data.jenisTahunBuku as 'NORMAL' | 'LAMPAU',
          StatusBuku: data.statusBuku as 'OPEN' | 'CLOSED',
          KarantinaCoa: karantinaCoa,
        });

        if (karantinaCoa) {
          // Sama dengan LoginPage: diarahkan ke satu-satunya menu yang terbuka. User di
          // bawah Tim IT mendapat pesan menghubungi Tim IT di halaman itu.
          message.warning(
            data.user.level >= LevelUser_81_TimIT
              ? 'Perusahaan ini masih memakai COA format lama. Hanya menu Pemeliharaan → Migrasi COA 9 Digit yang tersedia sampai migrasi selesai.'
              : 'Perusahaan ini masih memakai COA format lama dan perlu dimigrasi oleh Tim IT. Silakan hubungi Tim IT.',
            10,
          );
          navigate('/migrasi-coa', { replace: true });
          return;
        }

        navigate('/', { replace: true });
      } catch (err: unknown) {
        // Backend mengembalikan envelope { success:false, error } dengan status 401.
        // Axios membungkus body di err.response.data.
        const axiosResp = (err as { response?: { data?: SsoLoginEnvelope } }).response;
        const payload = axiosResp?.data?.error;
        setError(payload ?? {
          code: 'SSO_TOKEN_INVALID',
          message: 'Tidak bisa terhubung ke server. Coba login manual.',
        });
      }
    };

    exchange();
  }, [token, login, navigate, message]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradients.primaryGradient,
        padding: 24,
      }}
    >
      <Card style={{ width: 400, boxShadow: shadows.lg }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 8 }}>
            Booku
          </Title>
          <Text type="secondary">Sistem Akuntansi Terpadu</Text>
        </div>

        {!error ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>Sedang masuk dari Booku Client...</Text>
            </div>
          </div>
        ) : (
          <>
            <Alert
              type="error"
              showIcon
              message="Login SSO gagal"
              description={
                <>
                  <div>{error.message}</div>
                  <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                    Kode: {error.code}
                  </div>
                </>
              }
              style={{ marginBottom: 16 }}
            />
            <Button
              type="primary"
              block
              onClick={() => navigate('/login', { replace: true })}
            >
              Login Manual
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
